import { Close, FileDownload } from '@/components/icons'
import { APP_CUSTOM_ERROR } from '@/constants/MessageConstants'
import { inviteMembersCSV, useGetCsvTemplate } from '@/services'
import { downloadCsvTemplate } from '@/utils/csvTemplate'
import { Dialog, Transition } from '@headlessui/react'
import { Fragment, useState } from 'react'
import Dropzone from 'react-dropzone'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { ButtonPrimary } from '../button'
import { CsvTemplateColumnReference } from './CsvTemplateColumnReference'

type Props = {
  open: boolean
  onClose: () => void
  clubId: string
}

export const ImportPlayersModal = ({ open, onClose, clubId }: Props) => {
  const { t } = useTranslation()

  const [files, setFile] = useState<any>()

  const { data: template, isFetching: templateLoading } = useGetCsvTemplate('club-members-import', open)

  const handleDrop = (acceptedFile: any) => {
    setFile(acceptedFile)
  }

  const handleClose = () => {
    onClose()
    setFile([])
  }

  const onSubmit = async () => {
    try {
      const result = await inviteMembersCSV(clubId, files[0])
      if (result.status === 200) {
        handleClose()
        toast.success(result.body)
      } else {
        handleClose()
        toast.error(result.body)
      }
    } catch (error) {
      handleClose()
      toast.error(APP_CUSTOM_ERROR)
    }
  }
  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full flex flex-col gap-y-4 max-w-4xl transform overflow-hidden rounded-2xl bg-white px-6 py-4 text-left align-middle shadow-xl transition-all relative">
                <button
                  className="w-9 h-9 rounded-full bg-navy-900 bg-opacity-10 flex items-center justify-center ml-2 hover:bg-opacity-20 absolute right-2 top-2"
                  onClick={handleClose}
                >
                  <Close className="text-navy-900" />
                </button>
                <Dialog.Title as="h3" className="text-xl text-navy-900 font-semibold">
                  {t('club.importPlayers')}
                </Dialog.Title>

                <div className="flex flex-col gap-y-6">
                  <div className="flex flex-row">
                    <span className="text-sm text-navy-900 text-opacity-70">
                      {template?.result?.description ?? t('common.downloadCSVDesc')}{' '}
                      <a className="text-primary-500 text-opacity-70 cursor-pointer">{t('common.learnMore')}</a>
                    </span>
                    <button
                      className="px-3 py-2 bg-navy-900 bg-opacity-10 hover:bg-opacity-20 hover:transition-all hover:ease-in-out hover:duration-300 text-navy-900 text-sm font-semibold flex gap-x-1 items-center justify-center rounded-full whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!template?.result || templateLoading}
                      onClick={() => template?.result && downloadCsvTemplate(template.result)}
                    >
                      <FileDownload className="w-5 h-5 text-navy-900" />
                      {t('common.downloadTemplate')}
                    </button>
                  </div>
                  <CsvTemplateColumnReference template={template?.result} isLoading={templateLoading} />

                  {!files?.length ? (
                    <Dropzone onDrop={handleDrop}>
                      {({ getRootProps, getInputProps }) => (
                        <div
                          className="flex items-center justify-center rounded-3xl bg-navy-900 bg-opacity-10 py-12 cursor-pointer"
                          {...getRootProps()}
                        >
                          <input {...getInputProps()} />
                          <span className="text-xs text-navy-900 font-medium">
                            {t('common.dragFile')} <span className="text-primary-500">{t('common.browseFile')}</span>
                          </span>
                        </div>
                      )}
                    </Dropzone>
                  ) : (
                    <div>
                      <ul>
                        {files?.map((file: any) => (
                          <li key={file.name} className="text-navy-900 text-opacity-70 text-sm">
                            {file.name}
                          </li>
                        ))}
                      </ul>
                      <button
                        className="text-sm font-semibold text-primary-500 hover:text-opacity-70"
                        onClick={() => {
                          setFile([])
                        }}
                      >
                        {t('common.removeFile')}
                      </button>
                    </div>
                  )}

                  <div className="flex gap-x-6">
                    <button
                      type="button"
                      className={`bg-navy-900 hover:bg-opacity-20 bg-opacity-10 rounded-full flex flex-1 h-12 items-center justify-center text-navy-900 font-semibold`}
                      onClick={handleClose}
                    >
                      {t('common.cancel')}
                    </button>

                    <ButtonPrimary
                      title={t('club.importPlayers')}
                      className={`flex flex-1 h-12 items-center justify-center`}
                      onClick={onSubmit}
                      disabled={!files?.length}
                    />
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
